import type { Person, SquadDirectory } from '@tive/core';
import { hashKey, isRemote } from './utils';

/**
 * What the cards are coloured by.
 *
 * Colour answers "who belongs with whom" for a property the tree cannot show,
 * which is why a team is in here: its members are scattered across the chart on
 * purpose, and a shared colour is the only thing that draws them together.
 */
export type ColorBy =
  'none' | 'team' | 'department' | 'location' | 'workplace' | 'country' | 'level' | 'title';

export const COLOR_BY_OPTIONS: { value: ColorBy; label: string; hint: string }[] = [
  { value: 'none', label: 'Nothing', hint: 'Plain cards; the avatar still shows the department.' },
  { value: 'location', label: 'Location', hint: 'One colour per office, and grey for remote.' },
  { value: 'workplace', label: 'Office or remote', hint: 'Two colours: in an office, or not.' },
  { value: 'country', label: 'Country', hint: "Each country's own flag colour." },
  { value: 'team', label: 'Team', hint: 'One colour per team, wherever its people sit.' },
  { value: 'department', label: 'Department', hint: 'One colour per department.' },
  { value: 'level', label: 'Level', hint: 'Seniority read off the title: C suite down to intern.' },
  { value: 'title', label: 'Title', hint: 'One colour per exact job title. Expect repeats.' },
];

/** Shown on a card and in the legend when a person has no value for the dimension. */
export const NO_VALUE = 'Unassigned';

/**
 * The bucket a person falls into. Several teams is the awkward case: the card
 * can only be one colour, so it takes the first team alphabetically and the
 * legend says how many people that applies to. The team board is where somebody
 * genuinely appears twice.
 */
export function colorKeyFor(person: Person, colorBy: ColorBy, squads: SquadDirectory): string | null {
  switch (colorBy) {
    case 'none':
      return null;
    case 'department':
      return person.department?.trim() || NO_VALUE;
    case 'location':
      return person.location?.trim() || NO_VALUE;
    case 'workplace':
      return person.location ? (isRemote(person.location) ? 'Remote' : 'In an office') : NO_VALUE;
    case 'country':
      return person.country?.trim() || NO_VALUE;
    case 'level':
      return levelFor(person.title);
    case 'title':
      return person.title.trim() || NO_VALUE;
    case 'team': {
      const names = squads.squads
        .filter((squad) => squad.members.some((member) => member.personId === person.id))
        .map((squad) => squad.name)
        .sort((a, b) => a.localeCompare(b));
      return names[0] ?? NO_VALUE;
    }
    default: {
      const exhaustive: never = colorBy;
      return String(exhaustive);
    }
  }
}

// ---------------------------------------------------------------------------
// Dimensions that assign their own colours
// ---------------------------------------------------------------------------

/*
 * A hashed colour is fine when the buckets are arbitrary — one team against
 * another. It is not fine for a place somebody works or a flag they recognise:
 * those already mean something, and a hash that lands Norway and Mexico on the
 * same swatch is actively misleading. These are assigned by hand; hashing stays
 * the fallback for anything not named.
 */

const OFFICE_COLORS: Record<string, string> = {
  'us - boston hq': '#4285f5', // Tive Blue
  kosovo: '#3fdeaa', // Tive Green
  norway: '#f6794e', // Tive Orange
  mexico: '#f64e4e', // Tive Red
  china: '#9b59b6', // Purple
  'south africa': '#d9a24c', // Amber
  remote: '#8a8a8a', // Grey on purpose: it is not a place.
};

/**
 * Rippling writes "Norway Office" and "South Africa Office" but plain "Kosovo",
 * so the lookup is on a trimmed form rather than the literal string — a
 * location renamed slightly in the HRIS should not silently change colour.
 */
function officeKey(location: string): string {
  return location
    .toLowerCase()
    .replace(/\boffices?\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const WORKPLACE_COLORS: Record<string, string> = {
  'in an office': '#4285f5',
  remote: '#3fdeaa',
};

/**
 * Country to ISO code and the colour its flag is known by.
 *
 * The emoji is derived from the code rather than pasted in, so there is one
 * source of truth per country and no way to end up with a flag that disagrees
 * with its colour.
 *
 * "Known by" rather than "mostly": the United States flag is three colours and
 * this picks red, because a red-white-and-blue flag reduced to one swatch is
 * the red one. Collisions are unavoidable at this many countries — China and
 * Norway are both red — and the legend is what tells them apart.
 */
const COUNTRIES: Record<string, { iso: string; color: string }> = {
  kosovo: { iso: 'XK', color: '#244aa5' },
  'united states': { iso: 'US', color: '#b22234' },
  mexico: { iso: 'MX', color: '#006847' },
  norway: { iso: 'NO', color: '#ba0c2f' },
  'united kingdom': { iso: 'GB', color: '#012169' },
  china: { iso: 'CN', color: '#ee1c25' },
  ireland: { iso: 'IE', color: '#169b62' },
  germany: { iso: 'DE', color: '#ffce00' },
  'south africa': { iso: 'ZA', color: '#007a4d' },
  spain: { iso: 'ES', color: '#aa151b' },
  australia: { iso: 'AU', color: '#00247d' },
  netherlands: { iso: 'NL', color: '#ae1c28' },
  france: { iso: 'FR', color: '#0055a4' },
  switzerland: { iso: 'CH', color: '#d52b1e' },
  colombia: { iso: 'CO', color: '#fcd116' },
  albania: { iso: 'AL', color: '#e41e20' },
  turkey: { iso: 'TR', color: '#e30a17' },
  vietnam: { iso: 'VN', color: '#da251d' },
  czechia: { iso: 'CZ', color: '#11457e' },
  'czech republic': { iso: 'CZ', color: '#11457e' },
  canada: { iso: 'CA', color: '#ff0000' },
  poland: { iso: 'PL', color: '#dc143c' },
  sweden: { iso: 'SE', color: '#006aa7' },
  india: { iso: 'IN', color: '#ff9933' },
  brazil: { iso: 'BR', color: '#009c3b' },
  japan: { iso: 'JP', color: '#bc002d' },
  italy: { iso: 'IT', color: '#008c45' },
  portugal: { iso: 'PT', color: '#006600' },
  belgium: { iso: 'BE', color: '#fdda24' },
  denmark: { iso: 'DK', color: '#c8102e' },
  finland: { iso: 'FI', color: '#003580' },
  singapore: { iso: 'SG', color: '#ed2939' },
  israel: { iso: 'IL', color: '#0038b8' },
  'new zealand': { iso: 'NZ', color: '#00247d' },
  argentina: { iso: 'AR', color: '#74acdf' },
  chile: { iso: 'CL', color: '#0039a6' },
  romania: { iso: 'RO', color: '#002b7f' },
  ukraine: { iso: 'UA', color: '#0057b7' },
  austria: { iso: 'AT', color: '#ed2939' },
};

/** A flag emoji from an ISO country code, by way of the regional indicators. */
export function flagFor(country: string | null | undefined): string | null {
  const entry = country ? COUNTRIES[country.trim().toLowerCase()] : undefined;
  if (!entry) return null;
  return [...entry.iso].map((letter) => String.fromCodePoint(0x1f1e6 + letter.charCodeAt(0) - 65)).join('');
}

// ---------------------------------------------------------------------------
// Level
// ---------------------------------------------------------------------------

export const LEVELS = [
  'C suite',
  'VP',
  'Director',
  'Manager',
  'Lead',
  'Senior',
  'Mid-level',
  'Junior',
  'Intern',
] as const;

export type Level = (typeof LEVELS)[number];

/**
 * Seniority, read off the title, first match winning.
 *
 * The order is the whole thing: "Senior Director" is a director and "Managing
 * Director of Finance" is a director rather than a manager, so the rank words
 * are tried before the prefixes.
 *
 * Every acronym is matched on a word boundary, which is not fussiness. A
 * substring test for CTO also matches "dire(cto)r", and one for COO also
 * matches "(Coo)rdinator" — between them those two quietly reclassified every
 * director in the company as a C-suite executive when this was first written.
 */
const LEVEL_RULES: { level: Level; test: RegExp }[] = [
  { level: 'C suite', test: /\b(chief|ceo|cto|cfo|coo|cro|cmo|cpo|ciso|founder)\b/ },
  { level: 'VP', test: /\b(vp|svp|evp|vice\s+president)\b/ },
  // "Head of X" sits with the directors. It is what this company calls somebody
  // who owns a function rather than a team, which is a director's job whatever
  // the title says — and grouping them with managers put functional owners in
  // with the people reporting to them.
  { level: 'Director', test: /\b(director|head\s+of)\b/ },
  { level: 'Manager', test: /\bmanager\b/ },
  { level: 'Lead', test: /\b(lead|principal|architect)\b/ },
  { level: 'Intern', test: /\b(intern|internship|co-?op|trainee|apprentice)\b/ },
  { level: 'Senior', test: /^(senior|sr\.?|staff)\b/ },
  { level: 'Junior', test: /^(junior|jr\.?|associate|entry)\b/ },
];

/** The level a title reads as. A title with no rank word in it is mid-level. */
export function levelFor(title: string): Level | typeof NO_VALUE {
  const text = title.trim().toLowerCase();
  if (!text) return NO_VALUE;
  return LEVEL_RULES.find((rule) => rule.test.test(text))?.level ?? 'Mid-level';
}

/** A ladder rather than a jumble: cool and dark at the top, warm at the bottom. */
const LEVEL_COLORS: Record<Level, string> = {
  'C suite': '#6b2fa0',
  VP: '#9b59b6',
  Director: '#4285f5',
  Manager: '#42c5d4',
  Lead: '#1dc28b',
  Senior: '#3fdeaa',
  'Mid-level': '#8a8a8a',
  Junior: '#d9a24c',
  Intern: '#f6794e',
};

/** The colour this dimension assigns by hand, if it assigns any. */
function fixedColor(key: string, colorBy: ColorBy): string | undefined {
  const lower = key.trim().toLowerCase();
  switch (colorBy) {
    case 'location':
      return OFFICE_COLORS[officeKey(key)];
    case 'workplace':
      return WORKPLACE_COLORS[lower];
    case 'country':
      return COUNTRIES[lower]?.color;
    case 'level':
      return LEVEL_COLORS[key as Level];
    default:
      return undefined;
  }
}

/**
 * The categorical palette, lifted from tive-dashboard's `--color-series-*`.
 *
 * The brand's own blues and greens are not enough to tell a dozen teams apart,
 * which is the same wall the dashboard hit when it needed chart series; the
 * purple, cyan and amber here are the ones it added, so a colour means roughly
 * the same thing in both apps.
 *
 * Red and orange are usable now because the colour lives on a bar down the edge
 * of the card rather than in its background: a red *bar* is a category, a red
 * *ring* is still, unambiguously, somebody who moved.
 */
export const SERIES_COLORS = [
  '#4285f5', // Tive Blue
  '#3fdeaa', // Tive Green
  '#f6794e', // Tive Orange
  '#9b59b6', // Purple
  '#42c5d4', // Cyan
  '#d9a24c', // Amber
  '#f64e4e', // Tive Red
  '#1dc28b', // Tive Dark Green
] as const;

/**
 * The colour for a bucket: whatever was pinned to it, then whatever the
 * dimension assigns by hand, else a hashed one.
 *
 * Hashed rather than assigned in order, so a team keeps its colour as people
 * join and leave it, and both themes get the same one — a colour that changes
 * meaning between light and dark is worse than one that is slightly less
 * legible in one of them.
 */
export function swatchFor(
  key: string,
  overrides?: ReadonlyMap<string, string>,
  colorBy: ColorBy = 'none',
): string {
  return (
    overrides?.get(key) ??
    fixedColor(key, colorBy) ??
    (SERIES_COLORS[hashKey(key) % SERIES_COLORS.length] as string)
  );
}

/**
 * The colours teams have been pinned to, by name.
 *
 * Keyed by name because that is what the grouped tree carries — and because a
 * pinned colour usually exists to match something outside this app, like the
 * colour the team already has on a Jira board.
 */
export function colorOverrides(squads: SquadDirectory): Map<string, string> {
  const out = new Map<string, string>();
  for (const squad of squads.squads) if (squad.color) out.set(squad.name, squad.color);
  return out;
}

export interface LegendEntry {
  key: string;
  count: number;
}

/**
 * The buckets in view, largest first, with `Unassigned` pinned last. Capped by
 * the caller: a legend of forty titles is not a legend.
 *
 * Level is the exception: it is a ladder, so it reads top-down regardless of
 * how many people are on each rung.
 */
export function legendFor(people: Person[], colorBy: ColorBy, squads: SquadDirectory): LegendEntry[] {
  if (colorBy === 'none') return [];

  const counts = new Map<string, number>();
  for (const person of people) {
    const key = colorKeyFor(person, colorBy, squads);
    if (key === null) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const entries = [...counts].map(([key, count]) => ({ key, count }));
  if (colorBy === 'level') {
    // Anything off the ladder -- Unassigned -- goes last rather than first,
    // which is where indexOf's -1 would otherwise put it.
    const rank = (key: string) => {
      const index = (LEVELS as readonly string[]).indexOf(key);
      return index === -1 ? LEVELS.length : index;
    };
    return entries.sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key));
  }
  return entries.sort(compareEntries);
}

function compareEntries(a: LegendEntry, b: LegendEntry): number {
  if (a.key === NO_VALUE) return 1;
  if (b.key === NO_VALUE) return -1;
  return b.count - a.count || a.key.localeCompare(b.key);
}

/**
 * A swatch, thinned out so it can sit behind text.
 *
 * `color-mix` against `--color-surface` rather than a precomputed rgba, because
 * the card's own background is a different colour in each theme — an rgba would
 * be blended with whatever happened to be behind it, which in dark mode means a
 * tint that reads as grubby rather than tinted.
 *
 * Returned as a string for an inline style: these are runtime colours, so there
 * is no Tailwind class for them and no `@theme` token either.
 */
export function tintOf(swatch: string, percent: number): string {
  return `color-mix(in srgb, ${swatch} ${percent}%, var(--color-surface))`;
}
